import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { BhojanAddDonationDialog } from "@project/admin/components/bhojan-add-donation-dialog";
import { BhojanDonationDetailDialog } from "@project/admin/components/bhojan-donation-detail-dialog";
import type { Field } from "@project/admin/components/resource-table";
import { Button } from "@project/components/ui/button";
import { projectToast } from "@project/lib/toast";
import { generateReceiptPdf } from "@project/lib/receipt";
import { bhojanDonationService } from "@project/services/admin/bhojan-donation.service";
import {
  BHOJAN_DONATION_STATUS_OPTIONS,
  buildBhojanDonationAddress,
  getBhojanDonationAmount,
  getBhojanDonationDonorName,
  getBhojanDonationPackageLabel,
  getBhojanDonationPaymentMode,
  getBhojanDonationPhone,
  getBhojanDonationReceiptNo,
  normalizeBhojanDonation,
  type BhojanDonation,
} from "@project/types/bhojan";
import { Download, Eye, Plus } from "lucide-react";

const statusOptions = BHOJAN_DONATION_STATUS_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
}));

const fields: Field[] = [
  {
    name: "donor_name",
    label: "Donor Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile_number",
    label: "Phone",
    type: "tel",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "bhojan_name",
    label: "Package",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "category_name",
    label: "Category",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "preferred_sponsorship_date",
    label: "Preferred Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "preferred_tithi",
    label: "Preferred Tithi",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "payment_mode",
    label: "Payment Mode",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mode",
    label: "Mode",
    type: "select",
    options: [
      { value: "online", label: "Online" },
      { value: "offline", label: "Offline" },
    ],
    showInTable: true,
    readOnly: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: statusOptions,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

function downloadReceipt(row: BhojanDonation) {
  generateReceiptPdf({
    title: "Bhojanshala Donation Receipt",
    receiptNo: getBhojanDonationReceiptNo(row),
    date: String(row.created_at ?? row.donation_date ?? ""),
    donationScheme: getBhojanDonationPackageLabel(row),
    donorName: getBhojanDonationDonorName(row),
    donorMobile: getBhojanDonationPhone(row),
    donorEmail: row.email ?? "",
    donorAddress: buildBhojanDonationAddress(row),
    amount: getBhojanDonationAmount(row),
    donationType: row.category_name?.trim() ?? "Bhojanshala",
    paymentMode: getBhojanDonationPaymentMode(row),
    cashOrChequeNo: row.payments?.[0]?.razorpay_payment_id ?? "",
    preferredTithi: row.preferred_tithi?.trim() ?? "",
    occasion: row.occasion?.trim() ?? "",
  });
}

export default function Page() {
  const [selectedDonation, setSelectedDonation] =
    useState<BhojanDonation | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleViewDetails = async (row: BhojanDonation) => {
    setSelectedDonation(null);
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const details = await bhojanDonationService.get(row.id);
      setSelectedDonation(
        details
          ? normalizeBhojanDonation(details)
          : normalizeBhojanDonation(row)
      );
    } catch {
      setSelectedDonation(normalizeBhojanDonation(row));
      projectToast.error("Could not load full donation details");
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <AdminShell
      title="Bhojan Donations"
      subtitle="Successful Bhojanshala donation records from the payment gateway"
    >
      <ApiResourceTable<BhojanDonation>
        key={listKey}
        title="Donation"
        labelField="donor_name"
        fields={fields}
        searchFields={[
          "donor_name",
          "email",
          "mobile_number",
          "bhojan_name",
          "category_name",
          "display_name",
        ]}
        defaultValues={{}}
        statusFilterOptions={statusOptions}
        initialStatusFilter="success"
        csvFileName="bhojan-donations"
        toolbarActions={
          <Button
            type="button"
            size="sm"
            className="flex-1 gap-1 rounded-xl sm:flex-none"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Donation
          </Button>
        }
        list={async query => {
          const result = await bhojanDonationService.listPage(query);
          return {
            ...result,
            data: result.data.map(normalizeBhojanDonation),
          };
        }}
        renderRowActions={row => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="View Details"
              onClick={() => void handleViewDetails(row)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              title="Download Receipt"
              onClick={() => downloadReceipt(row)}
            >
              <Download className="h-4 w-4" />
              <span className="text-xs">Receipt</span>
            </Button>
          </div>
        )}
      />

      <BhojanAddDonationDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSuccess={() => setListKey(key => key + 1)}
      />

      <BhojanDonationDetailDialog
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        donation={selectedDonation}
        loading={loadingDetails}
        onDownloadReceipt={downloadReceipt}
      />
    </AdminShell>
  );
}
